<script setup lang="ts">
  import { computed, ref, nextTick } from 'vue'
  import {
    PencilIcon,
    EyeIcon,
    DocumentDuplicateIcon,
    ArrowUpIcon,
    ArrowDownIcon,
    TrashIcon,
    ArrowsUpDownIcon
  } from '@heroicons/vue/24/outline'
  import {
    ChevronDown,
    ChevronUp,
    ChevronRight,
    EllipsisVerticalIcon,
    X,
    ChevronsLeft,
    ChevronsUpDown,
    GripHorizontal,
    Ellipsis,
    CheckCircle2,
    Scale,
    User,
    Repeat,
    Link
  } from 'lucide-vue-next'
  import type {
    SuperUserQuestionnaire as Questionnaire,
    QuestionnaireSection,
    QuestionnaireQuestion
  } from '@/types/generated'
  import { QuestionnaireSectionTypeEnum } from '@/types/generated'
  import { useQuestionnaireEditorStore } from '@/store/admin/questionnaire/questionnaireEditor'
  import { useTranslation } from '@/composables/useTranslation'
  import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
    DropdownMenuSeparator
  } from '@/components/ui/dropdown-menu'
  import { Button } from '@/components/ui/button'
  import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'
  import { entitiesDE, entitiesEN } from '@/i18n/entity'
  import UnlinkFieldIndicator from '@/components/icons/UnlinkFieldIndicator.vue'
  const { t, locale } = useTranslation({
    en: {
      sections: 'Sections',
      questionnaireDetails: 'Questionnaire details',
      generalInformation: 'General information',
      collapseAllSections: 'Collapse all sections',
      hideSidebar: 'Hide sidebar',
      expandAll: 'Expand all',
      addQuestion: 'Add question',
      addSection: 'Add section',
      questions: 'questions',
      question: 'question',
      sectionActions: 'Section actions',
      questionActions: 'Question actions',
      rename: 'Rename',
      duplicate: 'Duplicate',
      moveUp: 'Move up',
      moveDown: 'Move down',
      delete: 'Delete',
      preview: 'Preview',
      moveToSection: 'Move to section',
      required: 'Required',
      collapseAll: 'Collapse all',
      lawSpecificSection: 'Law-specific section',
      participantSection: 'Participant section',
      virtualQuestionTitle: 'Would you like to add another {participantType}?',
      connectedFieldTooltip: 'Connected to case data field',
      unconnectedFieldTooltip: 'Not connected to any case data field',
      repeatableQuestionTooltip: 'Repeatable question for adding multiple participants',
      customers: 'Customers',
      newQuestion: 'New question',
      newSection: 'New section'
    },
    de: {
      sections: 'Abschnitte',
      questionnaireDetails: 'Fragebogen-Details',
      generalInformation: 'Allgemeine Informationen',
      collapseAllSections: 'Alle Abschnitte einklappen',
      hideSidebar: 'Seitenleiste ausblenden',
      expandAll: 'Alle ausklappen',
      addQuestion: 'Frage hinzufügen',
      addSection: 'Abschnitt hinzufügen',
      questions: 'Fragen',
      question: 'Frage',
      sectionActions: 'Abschnittsaktionen',
      questionActions: 'Fragenaktionen',
      rename: 'Umbenennen',
      duplicate: 'Duplizieren',
      moveUp: 'Nach oben',
      moveDown: 'Nach unten',
      delete: 'Löschen',
      preview: 'Vorschau',
      moveToSection: 'In Abschnitt verschieben',
      required: 'Erforderlich',
      collapseAll: 'Alle einklappen',
      lawSpecificSection: 'Rechtsspezifischer Abschnitt',
      participantSection: 'Beteiligten-Abschnitt',
      virtualQuestionTitle: 'Möchten Sie einen weiteren {participantType} hinzufügen?',
      connectedFieldTooltip: 'Mit Falldatenfeld verbunden',
      unconnectedFieldTooltip: 'Nicht mit einem Falldatenfeld verbunden',
      repeatableQuestionTooltip: 'Wiederholbare Frage zum Hinzufügen mehrerer Beteiligter',
      customers: 'Kunden',
      newQuestion: 'Neue Frage',
      newSection: 'Neuer Abschnitt'
    }
  })

  const props = defineProps<{
    questionnaire: Questionnaire
    isOpen: boolean
    showCustomersSection?: boolean
    customersSubtitle?: string
  }>()

  const emit = defineEmits<{
    toggle: []
  }>()

  const questionnaireEditorStore = useQuestionnaireEditorStore()
  const expandedSections = ref<Set<string>>(new Set())
  const editingSectionId = ref<string | null>(null)
  const editingSectionTitle = ref<string>('')
  const sectionTitleInput = ref<HTMLInputElement | null>(null)
  const selectedItem = computed(() => questionnaireEditorStore.selectedItem)

  // Helper function to get participant name for a section
  const getParticipantName = (section: QuestionnaireSection): string | null => {
    if ((section as any).type !== 'participant') return null
    const participantType = (section as any).participant_type
    if (!participantType) return null

    const entityTranslations = locale.value === 'de' ? entitiesDE : entitiesEN
    return entityTranslations[participantType as keyof typeof entityTranslations] || participantType
  }

  // Drag and drop state
  const draggedSection = ref<QuestionnaireSection | null>(null)
  const draggedQuestion = ref<{
    question: QuestionnaireQuestion
    fromSection: QuestionnaireSection
  } | null>(null)
  const dragOverSection = ref<string | null>(null)
  const dragOverQuestion = ref<string | null>(null)
  const isDragging = ref(false)
  const dragType = ref<'section' | 'question' | null>(null)

  const allSectionsExpanded = computed(() => {
    return (
      props.questionnaire.schema?.every((section) => expandedSections.value.has(section.uuid)) ??
      false
    )
  })

  const hasAnySectionExpanded = computed(() => {
    return expandedSections.value.size > 0
  })

  const availableSections = computed(() => {
    return (
      props.questionnaire.schema?.map((section) => ({
        uuid: section.uuid,
        title: section.title || 'Untitled Section'
      })) ?? []
    )
  })

  const toggleSection = (sectionUuid: string) => {
    if (expandedSections.value.has(sectionUuid)) {
      expandedSections.value.delete(sectionUuid)
    } else {
      expandedSections.value.add(sectionUuid)
    }
  }

  const toggleAllSections = () => {
    if (allSectionsExpanded.value) {
      expandedSections.value.clear()
    } else {
      props.questionnaire.schema?.forEach((section) => {
        expandedSections.value.add(section.uuid)
      })
    }
  }

  const addSectionAndExpand = () => {
    const currentLength = props.questionnaire.schema?.length || 0
    const newSection = questionnaireEditorStore.addSection(currentLength, t('newSection'))

    // Expand the newly added section
    if (newSection) {
      expandedSections.value.add(newSection.uuid)
    }
  }

  // Section inline editing functions
  const startEditingSection = async (section: QuestionnaireSection) => {
    editingSectionId.value = section.uuid
    editingSectionTitle.value = section.title || ''
    await nextTick()
    sectionTitleInput.value?.focus()
    sectionTitleInput.value?.select()
  }

  const saveSection = (section: QuestionnaireSection) => {
    if (editingSectionTitle.value.trim()) {
      section.title = editingSectionTitle.value.trim()
    }
    cancelEditingSection()
  }

  const cancelEditingSection = () => {
    editingSectionId.value = null
    editingSectionTitle.value = ''
  }

  const handleSectionKeydown = (event: KeyboardEvent, section: QuestionnaireSection) => {
    if (event.key === 'Enter') {
      event.preventDefault()
      saveSection(section)
    } else if (event.key === 'Escape') {
      event.preventDefault()
      cancelEditingSection()
    }
  }

  // Navigation handlers
  const selectQuestionnaireDetails = () => {
    questionnaireEditorStore.selectQuestionnaireDetails()
  }

  const selectCustomers = () => {
    questionnaireEditorStore.selectCustomers()
  }

  const selectSection = (section: QuestionnaireSection) => {
    questionnaireEditorStore.selectSection(section.uuid)
  }

  const selectQuestion = (question: QuestionnaireQuestion) => {
    questionnaireEditorStore.selectQuestion(question.uuid)
  }

  // Section actions
  const duplicateSection = (section: QuestionnaireSection) => {
    questionnaireEditorStore.duplicateSection(section)
  }

  const deleteSection = (section: QuestionnaireSection) => {
    questionnaireEditorStore.removeSection(section)
  }

  const moveSectionUp = (section: QuestionnaireSection) => {
    questionnaireEditorStore.moveSectionUp(section)
  }

  const moveSectionDown = (section: QuestionnaireSection) => {
    questionnaireEditorStore.moveSectionDown(section)
  }

  // Question actions
  const duplicateQuestion = (section: QuestionnaireSection, question: QuestionnaireQuestion) => {
    questionnaireEditorStore.copyQuestion(section, question)
  }

  const deleteQuestion = (section: QuestionnaireSection, question: QuestionnaireQuestion) => {
    questionnaireEditorStore.removeQuestion(section, question)
  }

  const moveQuestionUp = (section: QuestionnaireSection, question: QuestionnaireQuestion) => {
    questionnaireEditorStore.moveQuestionUp(section, question)
  }

  const moveQuestionDown = (section: QuestionnaireSection, question: QuestionnaireQuestion) => {
    questionnaireEditorStore.moveQuestionDown(section, question)
  }

  const moveQuestionToSection = (
    fromSection: QuestionnaireSection,
    toSection: QuestionnaireSection,
    question: QuestionnaireQuestion
  ) => {
    questionnaireEditorStore.moveQuestionToSection(fromSection, toSection, question)
  }

  // Helper functions
  const isFirstSection = (section: QuestionnaireSection) => {
    return props.questionnaire.schema?.indexOf(section) === 0
  }

  const isLastSection = (section: QuestionnaireSection) => {
    const index = props.questionnaire.schema?.indexOf(section)
    return index === (props.questionnaire.schema?.length ?? 0) - 1
  }

  const isFirstQuestion = (section: QuestionnaireSection, question: QuestionnaireQuestion) => {
    return section.items?.indexOf(question) === 0
  }

  const isLastQuestion = (section: QuestionnaireSection, question: QuestionnaireQuestion) => {
    const index = section.items?.indexOf(question)
    // If section is repeatable with a participant type, the virtual question is always last
    // so the last real question should have move down disabled
    return index === (section.items?.length ?? 0) - 1
  }

  // Check if section has a virtual question (repeatable with participant type)
  const hasVirtualQuestion = (section: QuestionnaireSection) => {
    return (section as any).repeatable === true && getParticipantName(section) !== null
  }

  // Drag and drop handlers for sections
  const onSectionDragStart = (event: DragEvent, section: QuestionnaireSection) => {
    // Only allow section dragging if we're actually dragging from the section header area
    draggedSection.value = section
    isDragging.value = true
    dragType.value = 'section'

    if (event.dataTransfer) {
      event.dataTransfer.effectAllowed = 'move'
      event.dataTransfer.setData('text/plain', section.uuid)
      event.dataTransfer.setData('type', 'section')

      // Create a simple purple drag image
      const dragElement = event.target as HTMLElement
      const clone = dragElement.cloneNode(true) as HTMLElement
      clone.style.background = '#8b5cf6'
      clone.style.color = 'white'
      clone.style.borderRadius = '8px'
      clone.style.boxShadow = '0 10px 15px -3px rgba(139, 92, 246, 0.3)'
      clone.style.position = 'absolute'
      clone.style.top = '-1000px'
      clone.style.left = '-1000px'
      document.body.appendChild(clone)
      event.dataTransfer.setDragImage(clone, 50, 20)

      // Remove the clone after drag starts
      setTimeout(() => document.body.removeChild(clone), 0)
    }
  }

  const onSectionDragOver = (event: DragEvent, section: QuestionnaireSection) => {
    event.preventDefault()

    // Only handle section drops if we're dragging a section
    const dragType = event.dataTransfer?.getData('type')
    if (dragType === 'section' && event.dataTransfer) {
      event.dataTransfer.dropEffect = 'move'
      dragOverSection.value = section.uuid
    }
  }

  const onSectionDragLeave = () => {
    dragOverSection.value = null
  }

  const onSectionDrop = (event: DragEvent, targetSection: QuestionnaireSection) => {
    event.preventDefault()

    // Only handle section drops if we're dragging a section
    const dragType = event.dataTransfer?.getData('type')
    if (dragType !== 'section') return

    if (draggedSection.value && draggedSection.value.uuid !== targetSection.uuid) {
      // Find the target index
      const targetIndex = props.questionnaire.schema?.indexOf(targetSection)

      if (targetIndex !== undefined && targetIndex !== -1) {
        // Use the store method to move the section
        questionnaireEditorStore.moveSectionToPosition(draggedSection.value, targetIndex)
      }
    }

    // Reset drag state
    draggedSection.value = null
    dragOverSection.value = null
  }

  const onSectionDragEnd = () => {
    draggedSection.value = null
    dragOverSection.value = null
    isDragging.value = false
    dragType.value = null
  }

  // Drag and drop handlers for questions
  const onQuestionDragStart = (
    event: DragEvent,
    question: QuestionnaireQuestion,
    section: QuestionnaireSection
  ) => {
    draggedQuestion.value = { question, fromSection: section }
    isDragging.value = true
    dragType.value = 'question'

    if (event.dataTransfer) {
      event.dataTransfer.effectAllowed = 'move'
      event.dataTransfer.setData('text/plain', question.uuid)
      event.dataTransfer.setData('type', 'question')

      // Create a simple purple drag image
      const dragElement = event.target as HTMLElement
      const clone = dragElement.cloneNode(true) as HTMLElement
      clone.style.background = '#ddd6fe'
      clone.style.border = '2px solid #8b5cf6'
      clone.style.color = '#5b21b6'
      clone.style.borderRadius = '8px'
      clone.style.boxShadow = '0 10px 15px -3px rgba(139, 92, 246, 0.3)'
      clone.style.position = 'absolute'
      clone.style.top = '-1000px'
      clone.style.left = '-1000px'
      document.body.appendChild(clone)
      event.dataTransfer.setDragImage(clone, 75, 25)

      // Remove the clone after drag starts
      setTimeout(() => document.body.removeChild(clone), 0)
    }
  }

  const onQuestionDragOver = (event: DragEvent, question: QuestionnaireQuestion) => {
    event.preventDefault()

    // Only handle question drops if we're dragging a question
    const dragType = event.dataTransfer?.getData('type')
    if (dragType === 'question' && event.dataTransfer) {
      event.dataTransfer.dropEffect = 'move'
      dragOverQuestion.value = question.uuid
    }
  }

  const onQuestionDragLeave = () => {
    dragOverQuestion.value = null
  }

  const onQuestionDrop = (
    event: DragEvent,
    targetQuestion: QuestionnaireQuestion,
    targetSection: QuestionnaireSection
  ) => {
    event.preventDefault()

    // Only handle question drops if we're dragging a question
    const dragType = event.dataTransfer?.getData('type')
    if (dragType !== 'question') return

    if (draggedQuestion.value && draggedQuestion.value.question.uuid !== targetQuestion.uuid) {
      const { question: draggedQ, fromSection } = draggedQuestion.value

      // If moving within the same section
      if (fromSection.uuid === targetSection.uuid) {
        const questions = [...(fromSection.items || [])]
        const draggedIndex = questions.indexOf(draggedQ)
        const targetIndex = questions.indexOf(targetQuestion)

        if (draggedIndex !== -1 && targetIndex !== -1) {
          // Remove from original position
          const [movedQuestion] = questions.splice(draggedIndex, 1)
          // Insert at new position
          questions.splice(targetIndex, 0, movedQuestion)
          // Update the section items
          fromSection.items = questions
        }
      } else {
        // Moving between different sections
        if (fromSection.items && targetSection.items) {
          const draggedIndex = fromSection.items.indexOf(draggedQ)
          const targetIndex = targetSection.items.indexOf(targetQuestion)

          if (draggedIndex !== -1) {
            // Remove from source section
            fromSection.items.splice(draggedIndex, 1)
            // Add to target section
            targetSection.items.splice(targetIndex, 0, draggedQ)
          }
        }
      }
    }

    // Reset drag state
    draggedQuestion.value = null
    dragOverQuestion.value = null
  }

  // Section-level drop handler for questions
  const onSectionQuestionDragOver = (event: DragEvent, section: QuestionnaireSection) => {
    event.preventDefault()

    // Only handle question drops if we're dragging a question
    const dragType = event.dataTransfer?.getData('type')
    if (dragType === 'question' && event.dataTransfer) {
      event.dataTransfer.dropEffect = 'move'
      dragOverSection.value = section.uuid
    }
  }

  const onSectionQuestionDrop = (event: DragEvent, targetSection: QuestionnaireSection) => {
    event.preventDefault()

    // Only handle question drops if we're dragging a question
    const dragType = event.dataTransfer?.getData('type')
    if (dragType !== 'question') return

    if (draggedQuestion.value) {
      const { question: draggedQ, fromSection } = draggedQuestion.value

      // If moving to a different section, add to the end
      if (fromSection.uuid !== targetSection.uuid) {
        if (fromSection.items && targetSection.items) {
          const draggedIndex = fromSection.items.indexOf(draggedQ)

          if (draggedIndex !== -1) {
            // Remove from source section
            fromSection.items.splice(draggedIndex, 1)
            // Add to target section at the end
            targetSection.items.push(draggedQ)
          }
        }
      }
    }

    // Reset drag state
    draggedQuestion.value = null
    dragOverQuestion.value = null
    dragOverSection.value = null
  }

  const onQuestionDragEnd = () => {
    draggedQuestion.value = null
    dragOverQuestion.value = null
    dragOverSection.value = null
    isDragging.value = false
    dragType.value = null
  }
</script>

<template>
  <div
    class="relative flex h-[83vh] w-[35vw] min-w-[400px] max-w-[450px] flex-col border-r border-gray-200
      bg-gray-50"
    :class="[{ hidden: !isOpen }, isDragging && 'overflow-hidden']"
  >
    <!-- Drag Overlay Effect -->
    <div
      v-if="isDragging"
      class="pointer-events-none absolute inset-0 z-40 bg-purple-500/5"
    >
      <!-- Floating Action Indicator -->
      <div class="absolute right-4 top-4 z-50">
        <div
          class="flex items-center gap-2 rounded-full border border-purple-200 bg-white/95 px-3 py-2 shadow-lg
            backdrop-blur-sm"
        >
          <div class="h-2 w-2 animate-pulse rounded-full bg-purple-500"></div>
          <span class="text-sm font-medium text-purple-700">
            {{ dragType === 'section' ? 'Moving section...' : 'Moving question...' }}
          </span>
        </div>
      </div>
    </div>
    <!-- Sidebar Header -->
    <div class="border-b border-gray-200 bg-white px-6 py-4">
      <div class="flex items-center justify-between">
        <h2 class="text-base font-semibold text-gray-900">
          {{ t('sections') }} ({{ questionnaire.schema?.length || 0 }})
        </h2>
        <div class="flex items-center space-x-2">
          <button
            class="flex h-8 w-8 items-center justify-center rounded-md border border-gray-200 bg-white text-black
              shadow-sm transition-colors hover:bg-gray-50 hover:text-gray-700"
            style="box-shadow: 0px 2px 4px 0px rgba(0, 0, 0, 0.04)"
            :title="hasAnySectionExpanded ? t('collapseAll') : t('expandAll')"
            @click="toggleAllSections"
          >
            <ChevronsUpDown class="h-4 w-4" />
          </button>
          <button
            class="flex h-8 w-8 items-center justify-center rounded-md border border-gray-200 bg-white text-black
              shadow-sm transition-colors hover:bg-gray-50 hover:text-gray-700"
            style="box-shadow: 0px 2px 4px 0px rgba(0, 0, 0, 0.04)"
            :title="t('hideSidebar')"
            @click="emit('toggle')"
          >
            <ChevronsLeft class="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>

    <!-- Sidebar Content -->
    <div class="relative flex-1 bg-gray-50">
      <!-- Scrollable Content -->
      <div class="h-[80vh] overflow-y-auto pb-20">
        <!-- Questionnaire Details Section -->
        <div
          class="mx-6 mt-6 flex h-[60px] cursor-pointer flex-col justify-center gap-1 rounded-lg border
            border-gray-300 p-3 transition-colors"
          :class="
            selectedItem === 'questionnaire-details' ? 'bg-gray-100' : 'bg-white hover:bg-gray-50'
          "
          @click="selectQuestionnaireDetails"
        >
          <h3 class="text-sm font-semibold text-gray-900">{{ t('questionnaireDetails') }}</h3>
          <p class="text-xs text-gray-500">{{ t('generalInformation') }}</p>
        </div>

        <!-- Customers Section (Admin only) -->
        <div
          v-if="showCustomersSection"
          class="mx-6 mt-4 flex h-[60px] cursor-pointer flex-col justify-center gap-1 rounded-lg border
            border-gray-300 p-3 transition-colors"
          :class="selectedItem === 'customers' ? 'bg-gray-100' : 'bg-white hover:bg-gray-50'"
          @click="selectCustomers"
        >
          <h3 class="text-sm font-semibold text-gray-900">{{ t('customers') }}</h3>
          <p class="text-xs text-gray-500">{{ customersSubtitle }}</p>
        </div>

        <!-- Sections List -->
        <div class="mt-6 px-6 pb-24">
          <div
            v-for="section in questionnaire.schema"
            :key="section.uuid"
            class="mb-4 cursor-pointer rounded-lg border border-gray-200 shadow-sm transition-colors"
            :class="[
              selectedItem === section.uuid ? 'bg-gray-100' : 'bg-white hover:bg-gray-50',
              dragOverSection === section.uuid && draggedQuestion
                ? 'scale-102 transform border-purple-400 bg-purple-50 shadow-lg ring-2 ring-purple-300/50'
                : dragOverSection === section.uuid
                  ? 'scale-102 transform border-purple-400 bg-purple-50 shadow-lg ring-2 ring-purple-300/50'
                  : '',
              draggedSection?.uuid === section.uuid
                ? 'scale-95 transform opacity-30 ring-4 ring-purple-400/30'
                : '',
              isDragging &&
                draggedSection?.uuid !== section.uuid &&
                'transform transition-all duration-300 hover:scale-105'
            ]"
            style="transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1)"
            @click="editingSectionId !== section.uuid && selectSection(section)"
          >
            <!-- Section Header -->
            <div
              class="flex items-center justify-between p-5"
              :draggable="editingSectionId !== section.uuid"
              @dragstart.stop="onSectionDragStart($event, section)"
              @dragover.stop="onSectionDragOver($event, section)"
              @dragleave.stop="onSectionDragLeave"
              @drop.stop="onSectionDrop($event, section)"
              @dragend.stop="onSectionDragEnd"
            >
              <div class="flex min-w-0 flex-1 items-center space-x-4">
                <GripHorizontal
                  class="h-5 w-5 transition-colors duration-200"
                  :class="[
                    draggedSection?.uuid === section.uuid
                      ? 'text-purple-600'
                      : isDragging
                        ? 'text-purple-400 hover:text-purple-600'
                        : 'text-gray-400 hover:text-purple-500'
                  ]"
                />

                <div class="min-w-0 flex-1">
                  <!-- Section Title - Inline Editing -->
                  <div
                    v-if="editingSectionId === section.uuid"
                    class="flex items-center space-x-2"
                    @dragstart.stop.prevent
                    @mousedown.stop
                  >
                    <input
                      ref="sectionTitleInput"
                      v-model="editingSectionTitle"
                      type="text"
                      class="flex-1 rounded-md border-0 px-3 py-1.5 text-sm font-semibold text-gray-900 ring-1 ring-inset
                        ring-purple-300 focus:ring-2 focus:ring-inset focus:ring-purple-600"
                      @blur="saveSection(section)"
                      @keydown="handleSectionKeydown($event, section)"
                      @dragstart.stop.prevent
                      @mousedown.stop
                    />
                    <Button
                      variant="ghost"
                      size="icon"
                      class="h-8 w-8 text-green-600 hover:text-green-700"
                      @click="saveSection(section)"
                    >
                      <CheckCircle2 class="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      class="h-8 w-8 text-red-600 hover:text-red-700"
                      @click="cancelEditingSection"
                    >
                      <X class="h-4 w-4" />
                    </Button>
                  </div>
                  <div
                    v-else
                    class="flex flex-col gap-2"
                  >
                    <span class="text-sm font-semibold leading-tight text-gray-900">
                      {{ section.title || 'Untitled Section' }}
                    </span>
                    <p class="text-xs text-gray-500">
                      {{
                        (section.items?.length || 0) +
                        ((section as any).repeatable && getParticipantName(section) ? 1 : 0)
                      }}
                      {{
                        (section.items?.length || 0) +
                          ((section as any).repeatable && getParticipantName(section) ? 1 : 0) ===
                        1
                          ? t('question')
                          : t('questions')
                      }}
                    </p>
                  </div>
                </div>
              </div>
              <div
                v-if="editingSectionId !== section.uuid"
                class="flex items-center space-x-2"
              >
                <!-- Section Type Icon with Tooltip -->
                <TooltipProvider v-if="section.type === QuestionnaireSectionTypeEnum.LAW_SPECIFIC">
                  <Tooltip>
                    <TooltipTrigger as-child>
                      <Scale class="h-5 w-5 flex-shrink-0 text-gray-500" />
                    </TooltipTrigger>
                    <TooltipContent
                      side="bottom"
                      variant="secondary"
                      :show-arrow="true"
                    >
                      {{ t('lawSpecificSection') }}
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
                <TooltipProvider
                  v-else-if="section.type === QuestionnaireSectionTypeEnum.PARTICIPANT"
                >
                  <Tooltip>
                    <TooltipTrigger as-child>
                      <User class="h-5 w-5 flex-shrink-0 text-gray-500" />
                    </TooltipTrigger>
                    <TooltipContent
                      side="bottom"
                      variant="secondary"
                      :show-arrow="true"
                    >
                      {{ t('participantSection')
                      }}{{
                        section.participant_type
                          ? `: ${section.participant_type.split('.').pop()}`
                          : ''
                      }}
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
                <!-- Section Actions Menu -->
                <DropdownMenu>
                  <DropdownMenuTrigger
                    class="flex h-8 w-8 items-center justify-center rounded-full bg-gray-100 text-gray-500 transition-colors
                      hover:bg-gray-200 hover:text-gray-700"
                  >
                    <button
                      class="flex h-8 w-8 items-center justify-center rounded-md border border-gray-200 bg-white text-black
                        shadow-sm transition-colors hover:bg-gray-50 hover:text-gray-700"
                      style="box-shadow: 0px 2px 4px 0px rgba(0, 0, 0, 0.04)"
                    >
                      <EllipsisVerticalIcon class="h-4 w-4" />
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent
                    align="end"
                    class="w-48"
                  >
                    <DropdownMenuItem @click="startEditingSection(section)">
                      <PencilIcon class="mr-2 h-4 w-4" />
                      {{ t('rename') }}
                    </DropdownMenuItem>
                    <DropdownMenuItem @click="duplicateSection(section)">
                      <DocumentDuplicateIcon class="mr-2 h-4 w-4" />
                      {{ t('duplicate') }}
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      :disabled="isFirstSection(section)"
                      @click="moveSectionUp(section)"
                    >
                      <ArrowUpIcon class="mr-2 h-4 w-4" />
                      {{ t('moveUp') }}
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      :disabled="isLastSection(section)"
                      @click="moveSectionDown(section)"
                    >
                      <ArrowDownIcon class="mr-2 h-4 w-4" />
                      {{ t('moveDown') }}
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem
                      class="text-red-600 focus:text-red-600"
                      @click="deleteSection(section)"
                    >
                      <TrashIcon class="mr-2 h-4 w-4" />
                      {{ t('delete') }}
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>

                <!-- Expand/Collapse Button -->
                <button
                  class="flex h-8 w-8 items-center justify-center rounded-md border border-gray-200 bg-white text-black
                    shadow-sm transition-colors hover:bg-gray-50 hover:text-gray-700"
                  style="box-shadow: 0px 2px 4px 0px rgba(0, 0, 0, 0.04)"
                  @click="toggleSection(section.uuid)"
                >
                  <ChevronUp
                    v-if="expandedSections.has(section.uuid)"
                    class="h-4 w-4"
                  />
                  <ChevronDown
                    v-else
                    class="h-4 w-4"
                  />
                </button>
              </div>
            </div>

            <!-- Section Questions -->
            <div
              v-if="expandedSections.has(section.uuid)"
              class="relative px-5 pb-10"
              @dragover="onSectionQuestionDragOver($event, section)"
              @dragleave="onSectionDragLeave"
              @drop="onSectionQuestionDrop($event, section)"
            >
              <!-- Questions List Container with Scroll -->
              <div class="max-h-60 space-y-1 overflow-y-auto pb-10">
                <!-- Empty section drop zone -->
                <div
                  v-if="
                    section.items?.length === 0 &&
                    draggedQuestion &&
                    dragOverSection === section.uuid
                  "
                  class="flex h-16 items-center justify-center rounded-lg border-2 border-dashed border-purple-400
                    bg-purple-50 text-sm font-medium text-purple-700"
                >
                  Drop question here
                </div>

                <div
                  v-for="question in section.items"
                  :key="question.uuid"
                  class="group flex cursor-pointer items-center gap-2"
                  :class="[
                    selectedItem === question.uuid
                      ? 'border-purple-300 bg-purple-100'
                      : 'hover:bg-gray-50',
                    dragOverQuestion === question.uuid
                      ? 'scale-105 transform border-purple-500 bg-purple-200 shadow-lg ring-2 ring-purple-300/50'
                      : '',
                    draggedQuestion?.question.uuid === question.uuid
                      ? 'scale-95 transform opacity-40 ring-4 ring-purple-400/40'
                      : '',
                    isDragging &&
                      draggedQuestion?.question.uuid !== question.uuid &&
                      'hover:scale-102 transition-all duration-200 hover:transform'
                  ]"
                  style="
                    padding: 8px;
                    border-radius: 8px;
                    align-self: stretch;
                    transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
                  "
                  :style="{
                    border:
                      selectedItem === question.uuid
                        ? '1px solid rgb(196 181 253)'
                        : dragOverQuestion === question.uuid
                          ? '1px solid #8b5cf6'
                          : '1px solid #e5e7eb',
                    background:
                      selectedItem === question.uuid
                        ? 'rgb(243 232 255)'
                        : dragOverQuestion === question.uuid
                          ? 'rgb(233 213 255)'
                          : '#fff'
                  }"
                  draggable="true"
                  @click.stop="selectQuestion(question)"
                  @dragstart.stop="onQuestionDragStart($event, question, section)"
                  @dragover.stop="onQuestionDragOver($event, question)"
                  @dragleave.stop="onQuestionDragLeave"
                  @drop.stop="onQuestionDrop($event, question, section)"
                  @dragend.stop="onQuestionDragEnd"
                >
                  <!-- Drag Handle -->
                  <GripHorizontal
                    class="h-5 w-5 flex-shrink-0 transition-colors duration-200"
                    :class="[
                      draggedQuestion?.question.uuid === question.uuid
                        ? 'text-purple-600'
                        : isDragging
                          ? 'text-purple-400 group-hover:text-purple-600'
                          : 'text-gray-400 group-hover:text-purple-500'
                    ]"
                  />
                  <div class="min-w-0 flex-1">
                    <p
                      class="truncate"
                      style="
                        color: #1f2937;
                        font-family: Inter;
                        font-size: 14px;
                        font-weight: 500;
                        line-height: 20px;
                      "
                    >
                      {{ question.label || 'Untitled Question' }}
                      <span
                        v-if="!question.optional"
                        style="
                          color: #ef4444;
                          font-family: Inter;
                          font-size: 14px;
                          font-weight: 500;
                          line-height: 20px;
                        "
                      >
                        *
                      </span>
                    </p>
                  </div>
                  <!-- Link/Unlink Indicator -->
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger as-child>
                        <div class="flex items-center justify-center">
                          <Link
                            v-if="question.tag && question.tag !== ''"
                            class="h-4 w-4 text-emerald-600"
                          />
                          <UnlinkFieldIndicator v-else />
                        </div>
                      </TooltipTrigger>
                      <TooltipContent
                        side="bottom"
                        variant="secondary"
                        :show-arrow="true"
                      >
                        {{
                          question.tag && question.tag !== ''
                            ? t('connectedFieldTooltip')
                            : t('unconnectedFieldTooltip')
                        }}
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>

                  <DropdownMenu>
                    <DropdownMenuTrigger
                      class="flex h-8 w-8 items-center justify-center text-black transition-colors hover:text-gray-700"
                      @click.stop
                    >
                      <Ellipsis class="h-4 w-4" />
                    </DropdownMenuTrigger>
                    <DropdownMenuContent
                      align="end"
                      class="w-48"
                    >
                      <DropdownMenuItem @click="selectQuestion(question)">
                        <EyeIcon class="mr-2 h-4 w-4" />
                        {{ t('preview') }}
                      </DropdownMenuItem>
                      <DropdownMenuItem @click="duplicateQuestion(section, question)">
                        <DocumentDuplicateIcon class="mr-2 h-4 w-4" />
                        {{ t('duplicate') }}
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        :disabled="isFirstQuestion(section, question)"
                        @click="moveQuestionUp(section, question)"
                      >
                        <ArrowUpIcon class="mr-2 h-4 w-4" />
                        {{ t('moveUp') }}
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        :disabled="isLastQuestion(section, question)"
                        @click="moveQuestionDown(section, question)"
                      >
                        <ArrowDownIcon class="mr-2 h-4 w-4" />
                        {{ t('moveDown') }}
                      </DropdownMenuItem>

                      <!-- Move to Section Submenu -->
                      <DropdownMenu v-if="availableSections.length > 1">
                        <DropdownMenuTrigger
                          class="flex w-full cursor-pointer select-none items-center rounded-sm px-2 py-1.5 text-sm outline-none
                            focus:bg-accent data-[state=open]:bg-accent"
                        >
                          <ArrowsUpDownIcon class="mr-2 h-4 w-4" />
                          {{ t('moveToSection') }}
                          <ChevronRight class="ml-auto h-4 w-4" />
                        </DropdownMenuTrigger>
                        <DropdownMenuContent
                          side="right"
                          align="start"
                          class="w-48"
                        >
                          <DropdownMenuItem
                            v-for="targetSection in availableSections.filter(
                              (s) => s.uuid !== section.uuid
                            )"
                            :key="targetSection.uuid"
                            @click="
                              moveQuestionToSection(
                                section,
                                questionnaire.schema?.find((s) => s.uuid === targetSection.uuid)!,
                                question
                              )
                            "
                          >
                            {{ targetSection.title }}
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>

                      <DropdownMenuSeparator />
                      <DropdownMenuItem
                        class="text-red-600 focus:text-red-600"
                        @click="deleteQuestion(section, question)"
                      >
                        <TrashIcon class="mr-2 h-4 w-4" />
                        {{ t('delete') }}
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>

                <!-- Virtual Question for Repeatable Sections -->
                <div
                  v-if="hasVirtualQuestion(section)"
                  class="group flex cursor-pointer items-center gap-2"
                  :class="[
                    selectedItem === `virtual-question-${section.uuid}`
                      ? 'border-purple-300 bg-purple-100'
                      : 'hover:bg-gray-50'
                  ]"
                  :style="{
                    padding: '8px',
                    borderRadius: '8px',
                    alignSelf: 'stretch',
                    border:
                      selectedItem === `virtual-question-${section.uuid}`
                        ? '1px solid rgb(196 181 253)'
                        : '1px solid #e5e7eb',
                    background:
                      selectedItem === `virtual-question-${section.uuid}`
                        ? 'rgb(243 232 255)'
                        : '#fff'
                  }"
                  @click.stop="questionnaireEditorStore.selectVirtualQuestion(section.uuid)"
                >
                  <!-- Drag Handle (visual only, not functional) -->
                  <GripHorizontal class="h-5 w-5 flex-shrink-0 text-gray-300" />
                  <div class="min-w-0 flex-1">
                    <p
                      class="truncate"
                      style="
                        color: #1f2937;
                        font-family: Inter;
                        font-size: 14px;
                        font-weight: 500;
                        line-height: 20px;
                      "
                    >
                      {{
                        t('virtualQuestionTitle', { participantType: getParticipantName(section) })
                      }}
                      <span
                        style="
                          color: #ef4444;
                          font-family: Inter;
                          font-size: 14px;
                          font-weight: 500;
                          line-height: 20px;
                        "
                      >
                        *
                      </span>
                    </p>
                  </div>
                  <!-- Repeat icon instead of link/unlink -->
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger as-child>
                        <div class="flex items-center justify-center">
                          <Repeat class="h-4 w-4 text-gray-400" />
                        </div>
                      </TooltipTrigger>
                      <TooltipContent
                        side="bottom"
                        variant="secondary"
                        :show-arrow="true"
                      >
                        {{ t('repeatableQuestionTooltip') }}
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                  <!-- More options (visual only, not functional) -->
                  <div class="flex h-8 w-8 items-center justify-center text-gray-300">
                    <Ellipsis class="h-4 w-4" />
                  </div>
                </div>
              </div>

              <!-- Sticky Add Question Button -->
              <div class="absolute bottom-0 left-0 right-0 border-gray-200 bg-gray-50 p-5">
                <button
                  class="flex w-full items-center justify-center space-x-2 text-sm text-gray-500 transition-colors
                    hover:text-gray-600"
                  style="
                    height: 32px;
                    padding: 6px;
                    border-radius: 6px;
                    border: 1px solid #e5e7eb;
                    background: #fff;
                    box-shadow: 0 2px 4px 0 rgba(0, 0, 0, 0.04);
                  "
                  @click="
                    questionnaireEditorStore.addQuestion(
                      section,
                      section.items?.length || 0,
                      t('newQuestion')
                    )
                  "
                >
                  <span>+</span>
                  <span>{{ t('addQuestion') }}</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Fixed Add Section Button -->
      <div class="sticky bottom-0 left-0 right-0 z-10 border-t border-gray-200 bg-gray-50 p-6">
        <button
          class="flex w-full items-center justify-center space-x-2 rounded-lg border border-gray-300 bg-white py-3
            text-sm text-gray-500 transition-colors hover:border-gray-400 hover:bg-gray-50 hover:text-gray-600"
          @click="addSectionAndExpand"
        >
          <span>+</span>
          <span>{{ t('addSection') }}</span>
        </button>
      </div>
    </div>
  </div>
</template>

<style scoped>
  /* Custom scale transforms for drag effects */
  .scale-102 {
    transform: scale(1.02);
  }

  .scale-105 {
    transform: scale(1.05);
  }

  .scale-95 {
    transform: scale(0.95);
  }
</style>
